import LoadingButton from "@mui/lab/LoadingButton"
import Autocomplete from "@mui/material/Autocomplete"
import Button from "@mui/material/Button"
import CheckBox from "@mui/material/Checkbox"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import FormControlLabel from "@mui/material/FormControlLabel"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../../common/axios"
import Column from "../../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../../common/components/ConditionalLinearProgress"
import CandidateDropdownOption from "../../components/CandidateDropdownOption"
import DialogHeader from "../../components/DialogHeader"

export default function AddCandidatesDialog({ open, onClose, workflow }) {
  const [isLoading, setIsLoading] = useState(false)
  const [candidates, setCandidates] = useState([])
  const [candidateInputValue, setCandidateInputValue] = useState("")
  const [selectedCandidates, setSelectedCandidates] = useState([])
  const [showQualifiedOnly, setShowQualifiedOnly] = useState(true)
  const [isAddingCandidates, setIsAddingCandidates] = useState(false)
  const axios = useAuthorizedAxiosClient()

  function handleSoftClose() {
    onClose()
  }

  function handleEnrollButtonClicked() {
    setIsAddingCandidates(true)
    axios
      .post(`/workflows/api/workflows/${workflow.id}/add-candidates/`, {
        candidate_ids: selectedCandidates.map((candidate) => candidate.id),
      })
      .then(({ data }) => {
        setSelectedCandidates([])
        onClose(data)
      })
      .finally(() => setIsAddingCandidates(false))
  }

  useEffect(
    () => {
      if (!open) {
        return
      }
      setIsLoading(true)
      const searchParams = new URLSearchParams()
      if (showQualifiedOnly) {
        searchParams.append("matching", "true")
      }
      axios
        .get(
          `/workflows/api/workflows/${
            workflow.id
          }/candidates/?${searchParams.toString()}`
        )
        .then(({ data }) => setCandidates(data))
        .finally(() => setIsLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, workflow, showQualifiedOnly]
  )

  return (
    <Dialog
      open={open}
      onClose={handleSoftClose}
      maxWidth="xs"
      fullWidth
      sx={{ "& .MuiPaper-root": { zIndex: 0 } }}
    >
      <DialogHeader title="Add Candidates" onClose={onClose} />
      <ConditionalPurpleLinearProgress visible={isLoading} />
      <DialogContent>
        <Column>
          <Typography variant="body1">
            Select the candidates you want to add to workflow:
          </Typography>
          <Autocomplete
            value={selectedCandidates}
            onChange={(_, newValue) => setSelectedCandidates(newValue)}
            inputValue={candidateInputValue}
            onInputChange={(_, newValue) => setCandidateInputValue(newValue)}
            options={candidates}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.candidate_name}
            getOptionKey={(option) => option.id}
            renderInput={(params) => (
              <TextField {...params} label="Select candidates" size="medium" />
            )}
            renderOption={(props, option) => (
              <CandidateDropdownOption option={option} props={props} />
            )}
            multiple
            filterSelectedOptions
            limitTags={2}
            size="small"
            sx={{ mt: 2 }}
          />
          <FormControlLabel
            control={
              <CheckBox
                checked={showQualifiedOnly}
                onChange={(e) => setShowQualifiedOnly(e.target.checked)}
              />
            }
            label="Only show candidates who qualify for this workflow, based on Entry Conditions."
            sx={{ mt: 2 }}
          />
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleSoftClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isAddingCandidates}
          onClick={handleEnrollButtonClicked}
        >
          Enroll
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
